// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { snakeCase } from "lodash";
import {
  action,
  comparer,
  makeObservable,
  observable,
  reaction,
  runInAction,
  toJS,
} from "mobx";

import { castToError, HydrationState } from "~hydration-utils";

import { getMethodologyCopy, getMetricCopy } from "../content";
import {
  dynamicFilterOptionMapToFilterType,
  DynamicFilterOptionMetadata,
  DynamicFilterOptionMetadataKey,
  DynamicFilterOptions,
  Filters,
  HydratablePathwaysMetric,
  MetricContent,
  MetricId,
  MetricRecord,
  NewBackendRecord,
  PageContent,
  PATHWAYS_PAGES,
  PathwaysMetricRecords,
  PathwaysPage,
  PopulationFilterValues,
} from "../index";
import {
  formatDateString,
  getTimePeriodRawValue,
  isAbortException,
  validateDynamicFilterOptions,
} from "../utils";
import { getMetricIdsForPage, getSectionIdForMetric } from "../views";
import {
  MetricsFetcher,
  PathwaysMetricStore,
  SharedMetricConstructorOptions,
  ShouldHydratePredicate,
} from "./types";

export default abstract class PathwaysNewBackendMetric<
  RecordFormat extends MetricRecord,
> implements HydratablePathwaysMetric
{
  readonly id: MetricId;

  readonly endpoint: string;

  readonly store: PathwaysMetricStore;

  readonly filters: Filters;

  readonly enableMetricModeToggle: boolean;

  readonly isHorizontal: boolean;

  readonly isGeographic: boolean;

  readonly rotateLabels: boolean;

  readonly accessorIsNotFilterType: boolean;

  /**
   * Filters this metric takes more than one value for, even though the filter
   * itself is single-select by default. A filter's own `isSingleSelect` is
   * fixed per tenant, but one chart can need several values of it where the
   * rest need exactly one — the over-time chart draws every calendar year the
   * reader picks, while each bar chart counts a single year.
   */
  readonly multiSelectFilters: readonly string[];

  protected allRecords?: RecordFormat[];

  // this is just a noop stub method to be overridden when needed
  protected dataTransformer: (d: RecordFormat[]) => RecordFormat[] = (
    d: RecordFormat[],
  ) => {
    return d;
  };

  lastUpdated?: Date;

  readonly isOverTime?: boolean;

  dynamicFilterOptions: Partial<DynamicFilterOptions>;

  protected abortController?: AbortController;

  private readonly fetchMetrics: MetricsFetcher<RecordFormat>;

  private readonly shouldHydrate: ShouldHydratePredicate;

  constructor({
    id,
    endpoint,
    store,
    fetchMetrics,
    shouldHydrate = () => true,
    filters,
    enableMetricModeToggle = false,
    isHorizontal = false,
    isGeographic = false,
    rotateLabels = false,
    accessorIsNotFilterType = false,
    multiSelectFilters = [],
  }: SharedMetricConstructorOptions<RecordFormat>) {
    this.id = id;
    this.endpoint = endpoint;
    this.store = store;
    this.fetchMetrics = fetchMetrics;
    this.shouldHydrate = shouldHydrate;
    this.filters = filters ?? store.filtersStore.enabledFilters[id];
    this.enableMetricModeToggle = enableMetricModeToggle;
    this.isHorizontal = isHorizontal;
    this.isGeographic = isGeographic;
    this.rotateLabels = rotateLabels;
    this.accessorIsNotFilterType = accessorIsNotFilterType;
    this.multiSelectFilters = multiSelectFilters;
    this.dynamicFilterOptions = {};

    makeObservable<PathwaysNewBackendMetric<RecordFormat>, "allRecords">(this, {
      allRecords: observable.ref,
      hydrate: action,
      hydrationState: observable,
    });

    reaction(
      () => {
        // Use toJS to ensure we access all the values of the filters. Reactions are only triggered
        // if data was accessed in the data function (this one), and so just returning
        // this.rootStore?.filters isn't enough to track a change to a property of filters.
        return {
          filters: toJS(this.store.filters),
          tenant: this.store.currentTenantId,
        };
      },
      () => {
        // Don't rehydrate the metric if we are not currently viewing it. Instead, clear its
        // hydration status so that the loading bar is displayed the next time we navigate to it.
        if (!this.isCurrentlyViewedMetric) {
          runInAction(() => {
            this.hydrationState = { status: "needs hydration" };
          });
          return;
        }
        this.hydrate();
      },
      {
        // Use a structural comparison instead of the default '==='. This actually compares the
        // values of the filters, and ensures that if a user clicks on the filter value that's
        // already selected, we don't make another API call (because the comparer will return false,
        // so the reaction won't be entered).
        // This solves a different problem than the toJS call above! The above one solves the
        // problem where we always think things are equal because we aren't seeing changes to filter
        // values at all. This one solves the problem where we always think things are different
        // because two objects with all the same properties don't compare as equal with ===.
        equals: comparer.structural,
      },
    );
  }

  get isCurrentlyViewedMetric(): boolean {
    const { page, section } = this.store;
    return (
      Object.keys(PATHWAYS_PAGES).includes(page) &&
      getMetricIdsForPage(page as PathwaysPage).includes(this.id) &&
      getSectionIdForMetric(this.id) === section
    );
  }

  getQueryParams(): URLSearchParams {
    const queryParams = new URLSearchParams();
    if (!this.store) {
      return queryParams;
    }
    const filterValues = this.store.filters;

    this.filters.enabledFilters.forEach((filter) => {
      const key = filter as keyof PopulationFilterValues;
      const values = filterValues[key];
      const queryKey = snakeCase(key);
      if (values) {
        values.forEach((val: string) => {
          if (queryKey === "time_period") {
            const timePeriod = getTimePeriodRawValue(val);
            if (timePeriod) {
              queryParams.append(`filters[${queryKey}]`, timePeriod);
            }
          } else if (val !== "ALL") {
            queryParams.append(`filters[${queryKey}]`, val);
          }
        });
      }
    });
    return queryParams;
  }

  abstract get dataSeries(): PathwaysMetricRecords;

  abstract get dataSeriesForDiffing(): RecordFormat[];

  abstract get isEmpty(): boolean;

  get tenantId(): string | undefined {
    return this.store.currentTenantId;
  }

  get content(): MetricContent {
    return getMetricCopy(this.tenantId)[this.id];
  }

  get chartTitle(): string {
    return this.content.title;
  }

  /**
   * Returns the note copy, unformatted. Child metric classes can override this
   * function and format the note if necessary.
   */
  get note(): string | undefined {
    return this.content.note;
  }

  get chartXAxisTitle(): string | undefined {
    return this.content.chartXAxisTitle;
  }

  get chartYAxisTitle(): string | undefined {
    return this.content.chartYAxisTitle;
  }

  /**
   * Returns the methodology copy specific to this metric.
   * Page methodology + metric methodology.
   */
  get methodology(): (PageContent | MetricContent)[] {
    if (!this.tenantId) return [];
    const methodology = getMethodologyCopy(this.tenantId).system;
    if (!methodology?.metricCopy || !methodology?.pageCopy) return [];

    return [
      methodology.pageCopy[this.store.page as PathwaysPage],
      methodology.metricCopy[this.id],
    ];
  }

  private parseDynamicFilterOptions(
    options: Partial<DynamicFilterOptionMetadata>,
  ): void {
    (
      Object.entries(options) as [DynamicFilterOptionMetadataKey, string][]
    ).forEach(([nameMapKey, optionValues]) => {
      const filterType = dynamicFilterOptionMapToFilterType[nameMapKey];

      if (!filterType || !optionValues) return;

      const filterOptions = JSON.parse(optionValues);
      if (!validateDynamicFilterOptions(filterOptions))
        throw new Error(
          `Invalid dynamic filter option for ${this.id}: ${filterType}`,
        );
      this.dynamicFilterOptions[filterType] = filterOptions;
    });
  }

  protected async fetchNewMetrics(
    params: URLSearchParams,
  ): Promise<NewBackendRecord<RecordFormat>> {
    // This could abort requests that have already completed, but that's not a big deal.
    this.abortController?.abort();
    this.abortController = new AbortController();

    return this.fetchMetrics(
      this.endpoint,
      params,
      this.abortController.signal,
    );
  }

  hydrationState: HydrationState = { status: "needs hydration" };

  /**
   * Fetches metric data and stores the result reactively on this Metric instance.
   *
   * True if the reader cleared every option of a filter this metric uses. No
   * record can match, so there is nothing to ask the backend for.
   */
  get hasEmptyFilterSelection(): boolean {
    if (!this.store) return false;
    const filterValues = this.store.filters;

    return this.filters.enabledFilters.some((filter) => {
      const values = filterValues[filter as keyof PopulationFilterValues];
      return Array.isArray(values) && values.length === 0;
    });
  }

  async hydrate(): Promise<void> {
    if (!this.shouldHydrate(this.id)) {
      return Promise.resolve();
    }
    this.hydrationState = { status: "loading" };

    if (this.hasEmptyFilterSelection) {
      runInAction(() => {
        this.allRecords = [];
        this.hydrationState = { status: "hydrated" };
      });
      return Promise.resolve();
    }

    this.fetchNewMetrics(this.getQueryParams())
      .then((fetchedData) => {
        runInAction(() => {
          this.allRecords = this.dataTransformer(fetchedData.data);
          this.lastUpdated = formatDateString(
            fetchedData.metadata?.lastUpdated,
          );
          if (fetchedData.metadata?.dynamicFilterOptions) {
            this.parseDynamicFilterOptions(
              JSON.parse(fetchedData.metadata.dynamicFilterOptions),
            );
          }
          this.hydrationState = { status: "hydrated" };
        });
      })
      .catch((e) => {
        // Just ignore abort exceptions because it means there is another request active.
        // This needs to be checked here rather than in fetchNewMetrics, otherwise the empty data
        // from the aborted request could override the actual data we want.
        if (!isAbortException(e)) {
          runInAction(() => {
            this.hydrationState = { status: "failed", error: castToError(e) };
          });
        }
      });
  }

  get records(): RecordFormat[] | undefined {
    return this.allRecords;
  }
}
